import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import { useState } from "react";
import { mediaUrl } from "@/lib/api";
import { useMall } from "@/context/MallContext";
import { themeOf } from "@/lib/mall";
import { markSource } from "@/lib/source";

const GROUPS = [
    ["own_sites", "Private sites", "Their own website, built here"],
    ["anchors", "Pro Shops", "Every live designer, their prices"],
    ["storefronts", "Storefronts", "Their products, listed here"],
    ["kiosks", "Starter shops", "Classified ads and classes"],
];

function Row({ card, unit, active, onHover }) {
    const { enter } = useMall();
    const navigate = useNavigate();
    const t = themeOf(card);
    const art = card.icon || card.logo;
    const go = () => { markSource("directory"); enter(card, "directory"); navigate(`/stores/${card.slug}`); };
    return (
        <button onClick={go} data-testid={`shop-row-${card.slug}`} data-active={active || undefined}
            onMouseEnter={() => onHover?.(card.slug)} onMouseLeave={() => onHover?.(null)}
            className={`group flex w-full items-center gap-3 border-b border-zinc-200 py-2.5 text-left transition-colors hover:bg-zinc-50 ${active ? "bg-zinc-50" : ""}`}>
            <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden border border-zinc-200 bg-white"
                style={active ? { borderColor: t.primary } : undefined}>
                {art ? <img src={mediaUrl(art)} alt="" className="h-full w-full object-cover" />
                    : <span className="font-display text-sm font-black" style={{ color: t.primary }}>{card.name.slice(0, 1)}</span>}
            </span>
            <span className="min-w-0 flex-1">
                <span className="block truncate font-display text-sm font-bold text-zinc-900">{card.name}</span>
                <span className="block truncate text-xs text-zinc-500">{card.city}{card.tagline ? ` · ${card.tagline}` : ""}</span>
            </span>
            {unit && <span className="shrink-0 font-mono text-[11px] text-zinc-400">{unit}</span>}
            <ArrowRight size={13} className="shrink-0 text-zinc-300 transition-transform group-hover:translate-x-1 group-hover:text-zinc-900" />
        </button>
    );
}

/**
 * The directory list: every shop, private sites first. The same list sits beside the map on
 * /directory and folds down to one line at the top of a shop's own page.
 */
export function ShopList({ floor, plan, activeSlug, onHover, collapsible = false }) {
    const [open, setOpen] = useState(!collapsible);
    const unitOf = (slug) => {
        const slot = plan?.slots?.find((s) => s.store?.slug === slug);
        if (slot) return slot.unit;
        const pad = (plan?.pads || []).find((p) => p.store?.slug === slug);
        return pad?.unit;
    };
    const groups = GROUPS.map(([key, label, sub]) => [key, label, sub, floor?.[key] || []]).filter(([, , , rows]) => rows.length);
    const total = groups.reduce((n, [, , , rows]) => n + rows.length, 0);

    return (
        <div data-testid="shop-list">
            {collapsible && (
                <button onClick={() => setOpen((o) => !o)} data-testid="shop-list-toggle" aria-expanded={open}
                    className="flex w-full items-center justify-between border border-zinc-300 bg-white px-3 py-2 text-left text-sm font-bold text-zinc-900 hover:border-zinc-900">
                    All {total} shops in the directory
                    <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
            )}
            {open && groups.map(([key, label, sub, rows]) => (
                <div key={key} className="mt-4 first:mt-0" data-testid={`shop-group-${key}`}>
                    <p className="flex items-baseline justify-between border-b-2 border-zinc-900 pb-1">
                        <span className="font-display text-sm font-extrabold uppercase tracking-wide text-zinc-900">{label} <span className="text-zinc-400">{rows.length}</span></span>
                        <span className="text-xs text-zinc-500">{sub}</span>
                    </p>
                    {rows.map((c) => <Row key={c.slug} card={c} unit={unitOf(c.slug)} active={c.slug === activeSlug} onHover={onHover} />)}
                </div>
            ))}
        </div>
    );
}
